import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { posPath } from '../posPath';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Smartphone, AlertCircle, Loader, ShieldCheck } from 'lucide-react';
import { accountHeaders, getAccountRestaurant } from '@/lib/pwaAccount';

const API = process.env.REACT_APP_BACKEND_URL;

// Generate device fingerprint
const getDeviceId = () => {
  let deviceId = localStorage.getItem('pos_device_id');
  if (!deviceId) {
    deviceId = `web_${navigator.userAgent}_${screen.width}x${screen.height}_${Date.now()}`.replace(/\s/g, '_');
    localStorage.setItem('pos_device_id', deviceId);
  }
  return deviceId;
};

// Every staff-step call carries the register's restaurant account token, so
// the server looks the cashier up in THAT restaurant only.
const staffHeaders = () => accountHeaders({ 'Content-Type': 'application/json' });

// The cashier must belong to the register's restaurant account.
const ofThisRestaurant = (employee) =>
  !!employee && String(employee.restaurant_id) === String(getAccountRestaurant()?.id);

// the staff PIN policy (backend services/pin_service: 4-8 digits); the back office sets 4-digit PINs
const PIN_MIN = 4;
const PIN_MAX = 8;

const POSLoginFlow = () => {
  const navigate = useNavigate();
  
  const [mode, setMode] = useState('check'); // check, trusted, new_device_phone, new_device_otp, new_device_pin
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [lockoutMinutes, setLockoutMinutes] = useState(0);

  const deviceId = getDeviceId();
  const deviceToken = localStorage.getItem('pos_device_token');
  // the trusted cashier's phone: the server finds the cashier by it
  const devicePhone = localStorage.getItem('pos_device_phone');

  useEffect(() => {
    // Check if this is a trusted device (read once, on arrival)
    if (localStorage.getItem('pos_device_token') && localStorage.getItem('pos_device_phone')) {
      setMode('trusted');
    } else {
      setMode('new_device_phone');
    }
  }, []);

  // Trusted Device: PIN Only Login
  const handleTrustedDeviceLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API}/api/rest/v1/pos/auth/login-pin`, {
        method: 'POST',
        headers: staffHeaders(),
        body: JSON.stringify({
          phone: devicePhone,
          pin,
          device_id: deviceId,
          device_token: deviceToken
        })
      });

      const data = await res.json();

      if (data.success && !ofThisRestaurant(data.employee)) {
        setError('This cashier is not on this restaurant\'s staff.');
      } else if (data.success) {
        // Store token
        localStorage.setItem('pos_token', data.token);
        localStorage.setItem('pos_user', JSON.stringify(data.employee));
        
        // Redirect to POS
        navigate(diningNext() || posPath(''), { replace: true });
      } else {
        if (data.requires_otp) {
          // Device not trusted anymore - need OTP
          setMode('new_device_phone');
          setError('Device not recognized. Phone verification required.');
        } else {
          setError(data.detail || data.message || 'Invalid PIN');
          
          // Check for lockout message
          const lockoutMatch = (data.detail || data.message)?.match(/(\d+) minutes/);
          if (lockoutMatch) {
            setLockoutMinutes(parseInt(lockoutMatch[1]));
          }
        }
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // New Device: Request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API}/api/rest/v1/pos/auth/request-login-otp`, {
        method: 'POST',
        headers: staffHeaders(),
        body: JSON.stringify({ phone, device_id: deviceId })
      });

      const data = await res.json();

      if (data.success) {
        setMode('new_device_otp');
      } else {
        setError(data.detail || data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // New Device: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API}/api/rest/v1/pos/auth/verify-login-otp`, {
        method: 'POST',
        headers: staffHeaders(),
        body: JSON.stringify({ phone, otp_code: otp, device_id: deviceId })
      });

      const data = await res.json();

      if (data.success) {
        // an allowed phone's first sign-in: it sets its PIN now (POS Login Access)
        setMode(data.requires_pin_setup ? 'new_device_set_pin' : 'new_device_pin');
        setPin('');
        setConfirmPin('');
        setOtp(''); // Clear OTP
      } else {
        setError(data.detail || data.message || 'Invalid OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // New Device: Login with existing PIN
  const handleNewDevicePINLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API}/api/rest/v1/pos/auth/login-pin`, {
        method: 'POST',
        headers: staffHeaders(),
        body: JSON.stringify({
          phone,
          pin,
          device_id: deviceId,
          device_name: `${navigator.userAgent.split(' ')[0]} Browser`,
          device_type: 'web'
        })
      });

      const data = await res.json();

      if (data.success && !ofThisRestaurant(data.employee)) {
        setError('This cashier is not on this restaurant\'s staff.');
      } else if (data.success) {
        // Store tokens
        localStorage.setItem('pos_token', data.token);
        localStorage.setItem('pos_user', JSON.stringify(data.employee));
        
        if (data.device_token) {
          localStorage.setItem('pos_device_token', data.device_token);
          localStorage.setItem('pos_device_phone', phone);
        }
        
        // Redirect to POS
        navigate(diningNext() || posPath(''), { replace: true });
      } else {
        setError(data.detail || data.message || 'Invalid PIN');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // First sign-in of an allowed phone: set the PIN (the texted code was just verified)
  const handleSetFirstPin = async (e) => {
    e.preventDefault();
    if (pin !== confirmPin) { setError('The two PINs do not match'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/rest/v1/pos/auth/set-first-pin`, {
        method: 'POST',
        headers: staffHeaders(),
        body: JSON.stringify({
          phone, pin, confirm_pin: confirmPin, device_id: deviceId,
          device_name: `${navigator.userAgent.split(' ')[0]} Browser`, device_type: 'web'
        })
      });
      const data = await res.json();
      if (data.success && !ofThisRestaurant(data.employee)) {
        setError('This phone is not on this restaurant\'s team.');
      } else if (data.success) {
        localStorage.setItem('pos_token', data.token);
        localStorage.setItem('pos_user', JSON.stringify(data.employee));
        if (data.device_token) {
          localStorage.setItem('pos_device_token', data.device_token);
          localStorage.setItem('pos_device_phone', phone);
        }
        navigate(diningNext() || posPath(''), { replace: true });
      } else if (data.requires_otp) {
        setMode('new_device_phone');
        setError('Please verify your phone again.');
      } else {
        setError(data.detail || data.message || 'Could not set the PIN');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (mode) {
      case 'check':
        return (
          <div className="text-center py-8">
            <Loader className="w-12 h-12 mx-auto animate-spin text-blue-600" />
            <p className="text-gray-600 mt-4">Checking device...</p>
          </div>
        );

      case 'trusted':
        return (
          <form onSubmit={handleTrustedDeviceLogin} className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <ShieldCheck className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-sm text-gray-600">Trusted Device</p>
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4" />
                Enter Your PIN
              </Label>
              <Input
                type="password"
                placeholder="••••••"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, PIN_MAX))}
                required
                maxLength={PIN_MAX}
                className="text-center text-3xl tracking-widest"
                autoFocus
              />
            </div>

            {lockoutMinutes > 0 && (
              <div className="p-4 bg-red-50 text-red-700 rounded flex items-start gap-3">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">PIN Locked</p>
                  <p className="text-sm">Too many failed attempts. Try again in {lockoutMinutes} minutes.</p>
                </div>
              </div>
            )}

            {error && !lockoutMinutes && (
              <div className="p-3 bg-red-50 text-red-700 rounded flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <Button 
              type="submit" 
              disabled={loading || pin.length < PIN_MIN || lockoutMinutes > 0}
              className="w-full h-12 text-lg"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>

            <button
              type="button"
              onClick={() => {
                localStorage.removeItem('pos_device_token');
                localStorage.removeItem('pos_device_phone');
                setMode('new_device_phone');
              }}
              className="w-full text-sm text-gray-600 hover:text-gray-900"
            >
              Not you? Use different account
            </button>
          </form>
        );

      case 'new_device_phone':
        return (
          <form onSubmit={handleRequestOTP} className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Smartphone className="w-8 h-8 text-blue-600" />
              </div>
              <p className="text-sm text-gray-600">New Device - Verification Required</p>
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Smartphone className="w-4 h-4" />
                Phone Number
              </Label>
              <Input
                type="tel"
                placeholder="+1 (234) 567-8900"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className="text-lg"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">
                We'll send a verification code
              </p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded flex items-start gap-2" data-testid="pos-login-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            {error && /POS Login Access/.test(error) && (
              <p className="text-xs text-gray-600" data-testid="pos-login-owner-hint">
                Owner: sign in to the back office, open Staff &amp; Labor &rarr; POS Login Access and add your own
                phone as Owner, then the phones of the team members who use the POS.
              </p>
            )}

            <Button type="submit" disabled={loading} className="w-full h-12 text-lg">
              {loading ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Verification Code'
              )}
            </Button>
          </form>
        );

      case 'new_device_otp':
        return (
          <form onSubmit={handleVerifyOTP} className="space-y-6">
            <div>
              <Label className="mb-2 block text-center">Enter Verification Code</Label>
              <Input
                type="text"
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                maxLength={6}
                className="text-center text-3xl tracking-widest font-mono"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Code sent to {phone}
              </p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setMode('new_device_phone')}
                className="flex-1"
              >
                Back
              </Button>
              <Button 
                type="submit" 
                disabled={loading || otp.length !== 6}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </Button>
            </div>

            <button
              type="button"
              onClick={handleRequestOTP}
              className="w-full text-sm text-blue-600 hover:underline"
            >
              Resend Code
            </button>
          </form>
        );

      case 'new_device_pin':
        return (
          <form onSubmit={handleNewDevicePINLogin} className="space-y-6">
            <div className="text-center mb-4">
              <p className="text-sm font-medium text-green-600">Phone Verified ✓</p>
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4" />
                Enter Your Existing PIN
              </Label>
              <Input
                type="password"
                placeholder="••••••"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, PIN_MAX))}
                required
                maxLength={PIN_MAX}
                className="text-center text-3xl tracking-widest"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1 text-center">
                The same PIN you use at the register
              </p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <Button 
              type="submit" 
              disabled={loading || pin.length < PIN_MIN}
              className="w-full h-12 text-lg"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login & Trust Device'
              )}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              This device will be trusted for future logins
            </p>
          </form>
        );

      case 'new_device_set_pin':
        return (
          <form onSubmit={handleSetFirstPin} className="space-y-5">
            <div className="text-center mb-2">
              <p className="text-sm font-medium text-green-600">Phone Verified ✓</p>
              <p className="text-sm text-gray-600 mt-1">First sign-in: create your POS PIN ({PIN_MIN}-{PIN_MAX} digits)</p>
            </div>
            <div>
              <Label className="flex items-center gap-2 mb-2"><Lock className="w-4 h-4" />New PIN</Label>
              <Input type="password" inputMode="numeric" placeholder="New PIN" value={pin} autoFocus
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, PIN_MAX))} maxLength={PIN_MAX}
                className="text-center text-3xl tracking-widest" data-testid="new-pin" required />
            </div>
            <div>
              <Label className="mb-2 block">Repeat PIN</Label>
              <Input type="password" inputMode="numeric" placeholder="Repeat PIN" value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, PIN_MAX))} maxLength={PIN_MAX}
                className="text-center text-3xl tracking-widest" data-testid="confirm-pin" required />
            </div>
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading || pin.length < PIN_MIN || confirmPin.length < PIN_MIN}
              className="w-full h-12 text-lg" data-testid="set-pin-submit">
              {loading ? <><Loader className="w-5 h-5 mr-2 animate-spin" />Saving...</> : 'Create PIN & Sign In'}
            </Button>
          </form>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center border-b pb-6">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center">
              <Lock className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold">POS Login</CardTitle>
          <p className="text-sm text-gray-600 mt-2">
            {mode === 'trusted' && 'Welcome back!'}
            {mode === 'new_device_phone' && 'Verify your identity'}
            {mode === 'new_device_otp' && 'Check your phone'}
            {mode === 'new_device_pin' && 'Enter your PIN'}
            {mode === 'new_device_set_pin' && 'Create your PIN'}
          </p>
        </CardHeader>

        <CardContent className="p-6">
          {renderContent()}
        </CardContent>

        <div className="px-6 pb-6 text-center">
          <p className="text-xs text-gray-500">
            Secured with trusted device authentication
          </p>
        </div>
      </Card>
    </div>
  );
};

// ?next=/dining/... (the Restaurant OS sign-in link) returns there after the PIN; nothing else is followed.
function diningNext() {
  const next = new URLSearchParams(window.location.search).get('next') || '';
  return /^\/dining(\/|$|\?)/.test(next) ? next : null;
}

export default POSLoginFlow;
